import {
    Heading,
    Icon,
    IconButton,
    Icons,
    ModalBody,
    ModalHeader,
    ThemeColorVariable,
    ThemeIconSizeVariable,
    ThemeSpaceVariable,
} from "@caspeco/casper-ui-library";
// eslint-disable-next-line caspeco-react/discourage-chakra-import
import { AspectRatio, ModalCloseButton } from "@chakra-ui/react";
import ImageWithSkeleton from "components/selection-components/image-with-skeleton/image-with-skeleton";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { AppMode, getAppMode } from "types/app-mode";

interface ICustomizeWizardModalBody {
    onBack?: () => void;
    children: React.ReactNode;
    headerImage?: string;
    headerTitle?: string;
    footerHeightSpacing?: string;
    /**
     * Rendered in a solid bar above the header image, beside the close control. Supplying it
     * moves the close control from floating over the image into that bar.
     */
    headerToolbar?: React.ReactNode;
    /** Steps back to whatever the guest was on before. Falls through to dismissing without one. */
    onStepBack?: () => void;
    /** Dismisses the modal. Supplying it puts a back control at the head of the toolbar row. */
    onDismiss?: () => void;
}

const appMode = getAppMode();

/**
 * wraps the wizard body in the modal wrappers and adjust the header to fit the design requirements
 */
const CustomizeWizardModalBody = ({
    children,
    onBack,
    headerImage,
    headerTitle,
    footerHeightSpacing = "88px",
    headerToolbar,
    onStepBack,
    onDismiss,
}: ICustomizeWizardModalBody) => {
    const { t } = useTranslation();
    const [showImage, setShowImage] = useState(true);
    const backButtonText = t("customizeWizard.backButtonText");
    const isAppModal = appMode === AppMode.Modal;
    const closeCustomizeModalIcon = isAppModal ? Icons.Back : Icons.Close;

    const headerImageElement = headerImage && showImage && (
        <AspectRatio maxW="100%" ratio={16 / 9}>
            <ImageWithSkeleton
                alt=""
                src={headerImage}
                onError={() => setShowImage(false)}
                width="100%"
                height="100%"
            />
        </AspectRatio>
    );

    if (headerToolbar) {
        const closeControl = (
            <ModalCloseButton position="relative" variant="ghost" flexShrink={0}>
                <Icon icon={closeCustomizeModalIcon} size={ThemeIconSizeVariable.Medium} />
            </ModalCloseButton>
        );

        // Always rendered, so following a suggestion never changes the header. In the SDK modal the
        // dismiss control is itself a back arrow, so this replaces it rather than sitting beside it.
        const backControl = onDismiss && (
            <IconButton
                variant="ghost"
                icon={Icons.Back}
                aria-label={t("back")}
                flexShrink={0}
                onClick={onStepBack ?? onDismiss}
            />
        );

        return (
            <>
                {/* Sibling of the body, not inside it, so the body's scrollbar does not run
                    alongside the bar. */}
                <ModalHeader
                    display="flex"
                    alignItems="center"
                    flexShrink={0}
                    // Overrides the recipe's flex-basis 0, which would pin the header at its 72px floor.
                    flexBasis="auto"
                    gap={ThemeSpaceVariable.Small}
                    p={ThemeSpaceVariable.Medium}
                    bg={ThemeColorVariable.Surface}
                    borderRadius={0}
                >
                    {backControl}
                    {headerToolbar}
                    {isAppModal ? null : closeControl}
                </ModalHeader>
                <ModalBody
                    display="flex"
                    flexDirection="column"
                    overflow="auto"
                    pb={footerHeightSpacing}
                    borderRadius={0}
                >
                    {headerImageElement}
                    {children}
                </ModalBody>
            </>
        );
    }

    return (
        <ModalBody
            display="flex"
            flexDirection="column"
            overflow="auto"
            pb={footerHeightSpacing}
            borderRadius={0}
        >
            {headerImage && showImage ? (
                <>
                    {headerImageElement}
                    <ModalCloseButton
                        position="absolute"
                        variant="ghost"
                        zIndex={1}
                        top={ThemeSpaceVariable.Medium}
                        left={isAppModal ? ThemeSpaceVariable.Medium : "auto"}
                        right={isAppModal ? "auto" : ThemeSpaceVariable.Medium}
                    >
                        <Icon icon={closeCustomizeModalIcon} size={ThemeIconSizeVariable.Medium} />
                    </ModalCloseButton>
                </>
            ) : (
                <ModalHeader
                    display="flex"
                    justifyContent="space-between"
                    p={ThemeSpaceVariable.Medium}
                >
                    {onBack ? (
                        <IconButton
                            variant="ghost"
                            icon={Icons.Back}
                            aria-label={backButtonText}
                            onClick={onBack}
                        />
                    ) : (
                        <ModalCloseButton
                            position="absolute"
                            variant="secondary"
                            zIndex={1}
                            top={ThemeSpaceVariable.Medium}
                            left={isAppModal ? ThemeSpaceVariable.Medium : "auto"}
                            right={isAppModal ? "auto" : ThemeSpaceVariable.Medium}
                        >
                            <Icon
                                icon={closeCustomizeModalIcon}
                                size={ThemeIconSizeVariable.Medium}
                            />
                        </ModalCloseButton>
                    )}
                    {headerTitle && <Heading as="h2">{headerTitle}</Heading>}
                </ModalHeader>
            )}
            {children}
        </ModalBody>
    );
};

export default CustomizeWizardModalBody;
